// Menu.tsx
import React, {
  forwardRef,
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import "./Menu.scss";
import "./Menu.collapsed.scss";
import "./Menu.portal.scss";
import { useNavigate, useLocation } from "react-router-dom";
import defaultMenuData, {
  keyboardMenuDataHuafenda,
  keyboardMenuDataBeiying,
  keyboardMenuDataZhwx,
  keyboardMenuDataSdcx,
  keyboardMenuDataRongyuan,
} from "./menuData";
import type { MenuItem, UseSdkApiTypes } from "./types";
import { Iconfont, useMessage } from "@eweadn-qiankun/shared-components";
import { Tooltip } from "../Tooltip";
import { feedbackImg } from "@eweadn-qiankun/shared-assets";
import ChildModal from "./ChildModal";
import DeviceStatus from "./DeviceStatus";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import { useElectronEnvironment } from "../../hooks/useElectronEnvironment";
import { useIsMacOS } from "../../hooks/useHostPlatform";
import {
  isBeiying916Or8815Device,
  isBeiyingDevice8815X98Keyboard,
  isBeiyingX988KRhythmFamily,
  markRouteSwitchStart,
  shouldResponsiveCollapseSidebar,
  resolveEquipmentWorkMode,
  supportsKeyboardSubAppMusicRhythm,
  supportsBeiying9168815MusicRhythm,
  isHfdAdvancedKeyboardAllowed,
  isHuafendaMagneticAxisEquipment,
  isEquipmentOtherMenuOnly,
  isHfdTftScreenDevice,
  isHfdTftScreenWirelessBlocked,
  trackClick,
  freqDeviceSupportsLighting,
  isBetaChannel,
} from "@eweadn-qiankun/shared-utils";
import { KEYBOARD_DEVICE_LAYOUT_REFRESH_EVENT } from "../../hooks/useKeyboardLayout";

/** 侧栏埋点默认产品线（ehub 子应用内为 ehub3） */
const PERF_PRODUCT_ROUTE = "ehub3";
const filterMusicDanceMenuUnlessSupported = (
  data: MenuItem[],
  musicRhythmSupported: boolean,
  desktopRhythmRuntime: boolean
): MenuItem[] =>
  musicRhythmSupported && desktopRhythmRuntime
    ? data
    : data.map((item) => ({
        ...item,
        children: item.children?.filter((child) => child.path !== "musicDance"),
      }));

const dropFirstMenuGroup = (data: MenuItem[]): MenuItem[] =>
  Array.isArray(data) && data.length > 0 ? data.slice(1) : data;

/** 与板载 Toast 分桶一致：仅当前 equipmentData 对应机型才展示切换提示 */
function getCurrentEquipmentDeviceKey(): string {
  try {
    const raw = localStorage.getItem("equipmentData");
    if (!raw) return "";
    const p = JSON.parse(raw) as Record<string, unknown>;
    const name = String(
      p.productName ?? p.displayname ?? p.deviceId ?? ""
    ).trim();
    if (name) return name;
    const vid = p.vendorId;
    const pid = p.productId;
    if (vid != null && pid != null) return `${vid}:${pid}`;
  } catch {
    /* ignore */
  }
  return "";
}

// ==================== 菜单项可见性规则 ====================

type DeviceMenuCtx = {
  keyboardType: string;
  solutionRovider: string;
  displayname: string;
  /** 华奋达 HID / 配置表 productName（与 displayname 可能不同，如 2.4G Dongle） */
  productName: string;
  workMode: string | undefined;
  hasScreen: boolean;
  /** 贝盈 8815 (X98_1K) */
  isX98_1K: boolean;
  /** 贝盈 916 / 8815 机芯 */
  isBeiying916Or8815: boolean;
  /** 当前设备 ID（localStorage currentDeviceId，用于 freq 判断等） */
  currentDeviceId: string;
  vendorId: number | undefined;
  productId: number | undefined;
  /** 华奋达设备 identifier（裸 ID，用于高级键双路径匹配） */
  identifier: string;
  /** 华奋达高级键查找 ID："{identifier}:{product}" 或 "{identifier}" */
  hfdId: string;
  /** 华奋达：是否磁轴（battle68 等）；机械轴为 false/undefined */
  magneticAxis?: boolean;
  keyboardSubAppMusicRhythmSupported: boolean;
  /** 通用 HID productName=keyboard 星闪：非 other 菜单项禁用（仍展示） */
  otherMenuOnly: boolean;
  /** 华奋达 TFT 屏幕：2.4G 下展示菜单但禁用 */
  tftScreenWirelessBlocked: boolean;
};

/** 华奋达有线连接时无性能配置的设备 */
const HFD_NO_PERF_WIRED = new Set([
  "3141:32774:X75 Pro",
  "3141:32978:EWEADN-X75Pro",
  "3141:32777:X75 Pro",
  "3141:32774:MUSE-M98",
  "3141:10001:MUSE-M98",
]);

/**
 * 按方案商分组的菜单项可见性规则。
 * 后续新增设备/方案商只需在此函数中追加对应分支。
 */
function shouldShowMenuItem(
  path: string | undefined,
  ctx: DeviceMenuCtx
): boolean {
  if (!path) return true;
  // 星闪 + otherMenuOnly：侧栏只保留「其他设置」
  if (ctx.otherMenuOnly && ctx.solutionRovider === "xingshan") {
    return path === "other";
  }
  // ===== 通用（不区分方案商）=====
  // otherMenuOnly：菜单项仍按常规规则展示，交互层再禁用非 other
  if (path === "musicDance" && !ctx.keyboardSubAppMusicRhythmSupported)
    return false;
  // 壁画：ES98(99) + X98 8K(98，非 8815/1K)
  if (path === "wallpaper") {
    const supportWallArt =
      ctx.keyboardType === "99" ||
      isBeiyingX988KRhythmFamily({
        keyboardType: ctx.keyboardType,
        displayname: ctx.displayname,
        vendorId: ctx.vendorId,
        productId: ctx.productId,
      });
    if (!supportWallArt) return false;
  }
  if (
    path === "screen" &&
    !ctx.hasScreen &&
    !isHfdTftScreenDevice({
      solutionRovider: ctx.solutionRovider,
      keyboardType: ctx.keyboardType,
      displayname: ctx.displayname,
      productName: ctx.productName,
      hasScreen: ctx.hasScreen,
    })
  ) {
    return false;
  }
  if (path === "lighting" && ctx.currentDeviceId === "x23zhuoyue") return false;

  const { solutionRovider } = ctx;

  // ===== 贝盈 =====
  if (solutionRovider === "beiying") {
    // 916 / 8815 机芯不展示高级键（音乐律动仅有线支持，由 keyboardSubAppMusicRhythmSupported 控制）
    if (ctx.isBeiying916Or8815 && path === "advancedKey") {
      return false;
    }
    // 8815(X98_1K) 不展示性能；916 开放性能页（无线休眠档位 + WASD 等）
    if (ctx.isX98_1K && path === "performance") {
      return false;
    }
    // 8815 不展示板载配置
    if (path === "boardConfig" && ctx.isX98_1K) return false;
  }

  // ===== 华奋达 =====
  if (solutionRovider === "huafenda") {
    // 仅特定设备开放高级键（经典 X87 已放行，页面内仅展示 SOCD）
    const isMagnetic = isHuafendaMagneticAxisEquipment({
      solutionRovider: "huafenda",
      identifier: ctx.identifier,
      displayname: ctx.displayname,
      keyboardType: ctx.keyboardType,
      magneticAxis: ctx.magneticAxis,
    });
    // 触发配置 / 测试校准：仅磁轴（battle68）；机械轴不展示
    if ((path === "trigger" || path === "testCalibration") && !isMagnetic) {
      return false;
    }
    // 磁轴（battle68）或 GIF 白名单机型开放高级键；机械轴默认不展示
    // 仅特定设备开放高级键（经典 X87 明确不支持）
    if (
      path === "advancedKey" &&
      !isHfdAdvancedKeyboardAllowed(ctx.hfdId, ctx.identifier, {
        displayname: ctx.displayname,
        keyboardType: ctx.keyboardType,
        magneticAxis: ctx.magneticAxis,
        productName: ctx.productName,
      })
    )
      return false;
    // M98/X75Pro/X75ProTFT 有线时无性能配置
    if (path === "performance" && HFD_NO_PERF_WIRED.has(ctx.hfdId))
      return false;
  }

  // ===== 达克 V80 =====
  if (solutionRovider === "dakai") {
    if (
      path === "quickConfig" ||
      path === "trigger" ||
      path === "testCalibration" ||
      path === "advancedKey" ||
      path === "boardConfig" ||
      path === "screen" ||
      path === "wallpaper" ||
      path === "musicDance"
    ) {
      return false;
    }
  }

  // ===== freq =====
  if (solutionRovider === "freq") {
    if (
      path === "lighting" &&
      !freqDeviceSupportsLighting(ctx.currentDeviceId)
    ) {
      return false;
    }
  }

  // ===== 智慧无限 =====
  if (solutionRovider === "ZHWX") {
    const zhwxAllowed = new Set([
      "boardConfig",
      "lighting",
      "changeKey",
      "macro",
      "performance",
      "other",
    ]);
    if (!zhwxAllowed.has(path)) return false;
  }

  // ===== SDCX（DK63 Machine）=====
  if (solutionRovider === "SDCX") {
    const sdcxAllowed = new Set([
      "boardConfig",
      "lighting",
      "changeKey",
      "macro",
      "other",
    ]);
    if (!sdcxAllowed.has(path)) return false;
  }

  // ===== 容园机械轴 =====
  if (solutionRovider === "rongyuan") {
    const allowed = new Set([
      "boardConfig",
      "lighting",
      "changeKey",
      "macro",
      "advancedKey",
      "performance",
      "other",
    ]);
    if (!allowed.has(path)) return false;
  }

  return true;
}

// ==================== 组件 ====================

export const Menu = forwardRef<HTMLElement, UseSdkApiTypes>((props, _ref) => {
  const {
    disabled,
    menuData: customMenuData,
    localStorageKey = "menuKey",
    menuFold = false,
    onMenuFold,
  } = props;
  const { t } = useTranslation();
  let keyboardType: any = null;
  let solutionRovider = "";
  let identifier = "";
  let displayname = "";
  let productName = "";
  let vendorId: number | undefined;
  let productId: number | undefined;
  let hasScreen: boolean = false;
  let workMode: string | undefined;
  let product: number | undefined;
  let machineBoard: string | undefined;
  let sdkType: number | undefined;
  let otherMenuOnly = false;
  let magneticAxis: boolean | undefined;

  const equipmentData: any = localStorage.getItem("equipmentData");
  if (equipmentData) {
    try {
      const parsed = JSON.parse(equipmentData);
      keyboardType = parsed.keyboardType;
      solutionRovider = parsed?.solutionRovider || "";
      identifier = parsed?.identifier || "";
      displayname = parsed?.displayname || "";
      productName = parsed?.productName || "";
      const v = Number(parsed?.vendorId);
      const p = Number(parsed?.productId);
      vendorId = Number.isFinite(v) ? v : undefined;
      productId = Number.isFinite(p) ? p : undefined;
      hasScreen = parsed?.hasScreen || false;
      workMode = parsed?.workMode;
      product = parsed?.product;
      machineBoard = parsed?.machineBoard;
      const st = Number(parsed?.sdkType);
      sdkType = Number.isFinite(st) ? st : undefined;
      otherMenuOnly = isEquipmentOtherMenuOnly(parsed);
      magneticAxis =
        parsed?.magneticAxis === true
          ? true
          : parsed?.magneticAxis === false
            ? false
            : undefined;
    } catch {
      /* ignore */
    }
  }
  /**
   * 仅 X98_1K（8815 机芯，VID/PID 见 beiyingX981K）隐藏「快捷配置」首组 + 高级键/性能等；
   * X98 8K（web-hid，同 keyboardType 98 + displayname X98）须保留菜单，勿再用字符串一刀切。
   */
  const isBeiyingX98_1K =
    solutionRovider === "beiying" &&
    (String(keyboardType || "").toLowerCase() === "x98_1k" ||
      isBeiyingDevice8815X98Keyboard({
        vendorId,
        productId,
        keyboardType,
        displayname,
      }));
  const currentDeviceId = (localStorage.getItem("currentDeviceId") || "")
    .toLowerCase()
    .trim();

  const keyboardSubAppMusicRhythmSupported =
    supportsKeyboardSubAppMusicRhythm({
      keyboardType,
      displayname,
      workMode: resolveEquipmentWorkMode({
        keyboardType,
        displayname,
        workMode,
      }),
    }) ||
    supportsBeiying9168815MusicRhythm({
      solutionRovider,
      machineBoard,
      vendorId,
      productId,
      keyboardType,
      sdkType,
      workMode: resolveEquipmentWorkMode({
        keyboardType,
        displayname,
        workMode,
      }),
    });
  const isBeiying916Or8815 = isBeiying916Or8815Device({
    solutionRovider,
    machineBoard,
    vendorId,
    productId,
    keyboardType,
    sdkType,
  });

  const tftScreenWirelessBlocked = isHfdTftScreenWirelessBlocked({
    solutionRovider,
    keyboardType,
    displayname,
    productName,
    hasScreen,
    workMode: resolveEquipmentWorkMode({
      keyboardType,
      displayname,
      workMode,
    }),
  });

  const menuCtx: DeviceMenuCtx = {
    keyboardType: String(keyboardType ?? ""),
    solutionRovider,
    displayname,
    productName,
    workMode,
    hasScreen,
    isX98_1K: isBeiyingX98_1K,
    isBeiying916Or8815,
    currentDeviceId,
    vendorId,
    productId,
    identifier,
    hfdId: product ? `${identifier}:${product}` : identifier,
    magneticAxis,
    keyboardSubAppMusicRhythmSupported,
    otherMenuOnly,
    tftScreenWirelessBlocked,
  };

  // 预声明所有菜单的翻译键
  const titleDict: Record<string, string> = {
    // keyboard
    "keyboard.menu.quickConfig": t("keyboard.menu.quickConfig"),
    "keyboard.menu.boardConfig": t("keyboard.menu.boardConfig"),
    "keyboard.menu.lighting": t("keyboard.menu.lighting"),
    "keyboard.menu.trigger": t("keyboard.menu.trigger"),
    "keyboard.menu.changeKey": t("keyboard.menu.changeKey"),
    "keyboard.menu.advancedKey": t("keyboard.menu.advancedKey"),
    "keyboard.menu.macro": t("keyboard.menu.macro"),
    "keyboard.menu.performance": t("keyboard.menu.performance"),
    "keyboard.menu.testCalibration": t("keyboard.menu.testCalibration"),
    "keyboard.menu.wallArt": t("keyboard.menu.wallArt"),
    "keyboard.menu.other": t("keyboard.menu.other"),
    "keyboard.menu.musicDance": t("keyboard.menu.musicDance"),
    "keyboard.menu.screen": t("keyboard.menu.screen"),
    // mouse
    "mouse.menu.boardConfig": t("mouse.menu.boardConfig"),
    "mouse.menu.changeKey": t("mouse.menu.changeKey"),
    "mouse.menu.dpiSetting": t("mouse.menu.dpiSetting"),
    "mouse.menu.dynamicSensitivity": t("mouse.menu.dynamicSensitivity"),
    "mouse.menu.performance": t("mouse.menu.performance"),
    "mouse.menu.triggerSetting": t("mouse.menu.triggerSetting"),
    "mouse.menu.macro": t("mouse.menu.macro"),
    "mouse.menu.parameter": t("mouse.menu.parameter"),
    "mouse.menu.lighting": t("mouse.menu.lighting"),
    "mouse.menu.other": t("mouse.menu.other"),
    // common group
    "common.advancedConfig": t("common.advancedConfig"),
  };

  const getTitle = (key: string | undefined) => {
    if (!key) return "";
    return titleDict[key] ?? key;
  };

  const getListTitle = (item: MenuItem) => {
    if (!item.title) return null;
    if (item.title === "common.advancedConfig") {
      return menuFold ? t("common.advancedConfigShort") : getTitle(item.title);
    }
    return getTitle(item.title);
  };

  /** 内测包：Electron 打开同一站点时带上的 ehubChannel。 */
  const showBetaFeedback = isBetaChannel();

  const handleBetaFeedbackClick = () => {
    (
      window as Window & {
        openBetaFeedbackModal?: () => void;
      }
    ).openBetaFeedbackModal?.();
  };

  const handleFeedbackClick = () => {
    (
      window as Window & {
        openFeedbackModal?: (entry?: "sidebar" | "settings_help") => void;
      }
    ).openFeedbackModal?.("sidebar");
  };
  // 使用传入的 menuData 或默认的 menuData
  // const menuData = customMenuData || defaultMenuData;
  const [menuData, setMenuData] = useState<MenuItem[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const [checkKey, setCheckKey] = useState("0");
  const [modalKey, setModalKey] = useState<string | null>(null);
  const isElectron = useElectronEnvironment();
  const isMac = useIsMacOS();
  const message = useMessage();
  const lastToastRef = useRef<{ type: string; key: any; time: number } | null>(
    null
  );

  const [viewportTick, setViewportTick] = useState(0);
  const [menuEquipmentRev, setMenuEquipmentRev] = useState(0);
  const [userExpandedOverride, setUserExpandedOverride] = useState(false);
  const prevTightRef = useRef(false);
  /** 收起态：Tooltip 挂到 body，避免任意祖先 overflow 裁剪 */
  const [sidebarTooltip, setSidebarTooltip] = useState<{
    text: string;
    left: number;
    top: number;
    wrapTwoLines?: boolean;
  } | null>(null);
  const menuMainRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const bump = () => setViewportTick((n) => n + 1);
    window.addEventListener("resize", bump);
    window.visualViewport?.addEventListener("resize", bump);
    return () => {
      window.removeEventListener("resize", bump);
      window.visualViewport?.removeEventListener("resize", bump);
    };
  }, []);

  const responsiveTight = useMemo(
    () => shouldResponsiveCollapseSidebar(),
    [viewportTick]
  );

  useEffect(() => {
    const tight = responsiveTight;
    const prev = prevTightRef.current;
    if (tight) {
      if (!userExpandedOverride) {
        onMenuFold?.(true);
      }
    } else if (prev) {
      setUserExpandedOverride(false);
      onMenuFold?.(false);
    }
    prevTightRef.current = tight;
  }, [responsiveTight, userExpandedOverride, onMenuFold]);

  useEffect(() => {
    if (!menuFold) {
      setSidebarTooltip(null);
    }
  }, [menuFold]);

  useEffect(() => {
    const hide = () => setSidebarTooltip(null);
    const main = menuMainRef.current;
    main?.addEventListener("scroll", hide, { passive: true });
    window.addEventListener("scroll", hide, { passive: true, capture: true });
    window.addEventListener("resize", hide, { passive: true });
    window.addEventListener("blur", hide);
    return () => {
      main?.removeEventListener("scroll", hide);
      window.removeEventListener("scroll", hide, { capture: true });
      window.removeEventListener("resize", hide);
      window.removeEventListener("blur", hide);
    };
  }, []);

  const handleSidebarToggleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!onMenuFold) return;
    if (menuFold) {
      onMenuFold(false);
      if (responsiveTight) {
        setUserExpandedOverride(true);
      }
    } else {
      onMenuFold(true);
      setUserExpandedOverride(false);
    }
  };

  useEffect(() => {
    const shouldShowToast = (type: string, key: any) => {
      const now = Date.now();
      if (
        lastToastRef.current &&
        lastToastRef.current.type === type &&
        lastToastRef.current.key === key &&
        now - lastToastRef.current.time < 1000
      ) {
        return false;
      }
      lastToastRef.current = { type, key, time: now };
      return true;
    };

    const handleKbChange = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const key = detail?.key;
      const name = detail?.name;
      const source = detail?.source;

      if (source === "sync") return;

      const eventDeviceKey = detail?.deviceKey as string | undefined;
      const currentDeviceKey = getCurrentEquipmentDeviceKey();
      if (
        eventDeviceKey &&
        currentDeviceKey &&
        eventDeviceKey !== currentDeviceKey
      ) {
        return;
      }

      if (!shouldShowToast("keyboard", key)) return;

      const label = t("keyboard.boardConfig.localConfigLabel", {
        num: (typeof key === "number" ? key : 0) + 1,
      });
      message.success(
        t("menu.boardList.switchSuccess", { label: name || label })
      );
    };

    const handleMouseChange = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const profile = detail?.profile;
      const name = detail?.name;
      const source = detail?.source;

      if (source === "sync") return;

      const eventDeviceKey = detail?.deviceKey as string | undefined;
      const currentDeviceKey = getCurrentEquipmentDeviceKey();
      if (
        eventDeviceKey &&
        currentDeviceKey &&
        eventDeviceKey !== currentDeviceKey
      ) {
        return;
      }

      if (!shouldShowToast("mouse", profile)) return;

      const label = t("mouse.boardConfig.localConfigLabel", {
        num: (typeof profile === "number" ? profile : 0) + 1,
      });
      message.success(
        t("menu.boardList.switchSuccess", { label: name || label })
      );
    };

    window.addEventListener("keyboard-board-config-changed", handleKbChange);
    window.addEventListener("mouse-board-profile-changed", handleMouseChange);

    return () => {
      window.removeEventListener(
        "keyboard-board-config-changed",
        handleKbChange
      );
      window.removeEventListener(
        "mouse-board-profile-changed",
        handleMouseChange
      );
    };
  }, [t, message]);

  const handleLogoClick = useCallback(() => {
    const run = async () => {
      try {
        const pathname =
          typeof window !== "undefined" ? window.location.pathname : "";
        const moduleId = pathname.includes("keyboard-management")
          ? "keyboard-management"
          : pathname.includes("mouse-management")
            ? "mouse-management"
            : "hub-homepage";
        const appType = pathname.includes("keyboard-management")
          ? "keyboard"
          : pathname.includes("mouse-management")
            ? "mouse"
            : "hub-homepage";
        void trackClick({
          moduleId,
          elementId: "menu/back_home",
          appType,
          deviceType:
            appType === "keyboard"
              ? "keyboard"
              : appType === "mouse"
                ? "mouse"
                : undefined,
        });
      } catch {
        // ignore
      }
      try {
        await props?.onBeforeBackToDeviceList?.();
      } catch (e) {
        console.warn("[Menu] onBeforeBackToDeviceList failed:", e);
      }
      if (props?.onNavigateToDeviceHome) {
        props.onNavigateToDeviceHome();
        return;
      }
      try {
        if (typeof window !== "undefined") {
          window.__eweadnSuppressHomeHidAutoconnect = true;
          window.sessionStorage.setItem(
            "eweadn:skipHomeHidAutoconnectOnce",
            "1"
          );
          localStorage.removeItem("isConnecting");
          window.dispatchEvent(new Event("eweadn:suppressHomeHidAutoconnect"));
        }
      } catch {
        // ignore
      }
      window.location.href = "/";
    };
    void run();
  }, [props]);
  useEffect(() => {
    let menuKeyboardType = keyboardType;
    let menuDisplayname = displayname;
    let menuWorkMode = workMode;
    let menuSolution = solutionRovider;
    try {
      const raw = localStorage.getItem("equipmentData");
      if (raw) {
        const parsed = JSON.parse(raw);
        menuKeyboardType = parsed.keyboardType;
        menuDisplayname = parsed.displayname ?? parsed.displayName;
        menuWorkMode = parsed?.workMode ?? "";
        menuSolution = parsed?.solutionRovider ?? "";
      }
    } catch {
      // ignore
    }

    const musicRhythmSupported =
      supportsKeyboardSubAppMusicRhythm({
        keyboardType: menuKeyboardType,
        displayname: menuDisplayname,
        workMode: resolveEquipmentWorkMode({
          keyboardType: menuKeyboardType,
          displayname: menuDisplayname,
          workMode: menuWorkMode,
        }),
      }) ||
      supportsBeiying9168815MusicRhythm({
        solutionRovider: menuSolution,
        keyboardType: menuKeyboardType,
        displayname: menuDisplayname,
        workMode: resolveEquipmentWorkMode({
          keyboardType: menuKeyboardType,
          displayname: menuDisplayname,
          workMode: menuWorkMode,
        }),
      });

    // 如果传入了 customMenuData，直接使用，不需要根据 equipmentData 切换
    if (customMenuData) {
      const targetMenuData = isBeiyingX98_1K
        ? dropFirstMenuGroup(customMenuData)
        : customMenuData;
      setMenuData(
        filterMusicDanceMenuUnlessSupported(
          targetMenuData,
          musicRhythmSupported,
          isElectron
        )
      );
      return;
    }

    // 只有在没有传入 customMenuData 时，才根据 equipmentData 切换键盘菜单
    const baseMenu =
      menuSolution === "huafenda"
        ? keyboardMenuDataHuafenda || []
        : menuSolution === "beiying"
          ? keyboardMenuDataBeiying || []
          : menuSolution === "ZHWX"
            ? keyboardMenuDataZhwx || []
            : menuSolution === "SDCX"
              ? keyboardMenuDataSdcx || []
              : menuSolution === "rongyuan"
                ? keyboardMenuDataRongyuan || []
                : defaultMenuData || [];
    const menuWithoutFirstGroup = isBeiyingX98_1K
      ? dropFirstMenuGroup(baseMenu)
      : baseMenu;

    setMenuData(
      filterMusicDanceMenuUnlessSupported(
        menuWithoutFirstGroup,
        musicRhythmSupported,
        isElectron
      )
    );
  }, [
    customMenuData,
    isElectron,
    keyboardType,
    displayname,
    workMode,
    isBeiyingX98_1K,
    location.pathname,
    menuEquipmentRev,
  ]);

  useEffect(() => {
    const onEquipmentRefresh = () => setMenuEquipmentRev((n) => n + 1);
    window.addEventListener(
      KEYBOARD_DEVICE_LAYOUT_REFRESH_EVENT,
      onEquipmentRefresh
    );
    return () => {
      window.removeEventListener(
        KEYBOARD_DEVICE_LAYOUT_REFRESH_EVENT,
        onEquipmentRefresh
      );
    };
  }, []);

  // 根据路径查找对应的菜单项key
  const findMenuKeyByPath = (path: string): string | null => {
    for (const item of menuData) {
      if (item.children) {
        for (const child of item.children) {
          if (child.path && path.includes(child.path)) {
            return child.key;
          }
        }
      }
    }
    return null;
  };

  const isOtherMenuOnlyXingshan =
    menuCtx.otherMenuOnly && menuCtx.solutionRovider === "xingshan";

  const isMenuItemInteractionDisabled = (item: MenuItem) => {
    if (disabled) return true;
    if (isOtherMenuOnlyXingshan && item.path && item.path !== "other") {
      return true;
    }
    if (item.path === "screen" && menuCtx.tftScreenWirelessBlocked) {
      return true;
    }
    return false;
  };

  const screenWiredOnlyTip = t("keyboard.screen.wiredOnlyTip");

  const menuCheck = (item: MenuItem) => {
    if (isMenuItemInteractionDisabled(item)) return;
    // 板载配置：点击跳转路由，hover显示弹窗
    if (item.path === "boardConfig") {
      setCheckKey(item.key);
      localStorage.setItem(localStorageKey, item.key);
      if (item.path) {
        markRouteSwitchStart(item.path, PERF_PRODUCT_ROUTE);
        navigate(item.path);
      }
      return;
    }

    // 用户信息：点击切换弹窗显示/隐藏
    if (item.modal) {
      if (modalKey === item.key) {
        setModalKey(null);
      } else {
        setModalKey(item.key);
      }
      return;
    }

    setCheckKey(item.key);
    localStorage.setItem(localStorageKey, item.key);
    if (item.path) {
      markRouteSwitchStart(item.path, PERF_PRODUCT_ROUTE);
      navigate(item.path);
    }
  };

  useEffect(() => {
    // 根据当前路由同步侧边栏选中状态
    const currentPath = location.pathname;
    const keyFromPath = findMenuKeyByPath(currentPath);

    if (keyFromPath) {
      setCheckKey(keyFromPath);
      localStorage.setItem(localStorageKey, keyFromPath);
    } else {
      const menuKey = localStorage.getItem(localStorageKey);
      if (menuKey) {
        setCheckKey(menuKey);
      } else {
        // 使用第一个有 children 的菜单项的第一个 child 作为默认值
        const defaultKey =
          menuData.find(
            (item: MenuItem) => item.children && item.children.length > 0
          )?.children?.[0]?.key || "4-1";
        localStorage.setItem(localStorageKey, defaultKey);
        setCheckKey(defaultKey);
      }
    }
  }, [location.pathname, localStorageKey, menuData]);

  // 点击外部关闭弹窗
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // 检查点击是否在弹窗或菜单项内部
      if (
        !target.closest(".child-modal-box") &&
        !target.closest(".child-item")
      ) {
        setModalKey(null);
      }
    };

    if (modalKey) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [modalKey]);

  return (
    <div
      className={`menu-box ${menuFold ? "menu-box--collapsed" : "menu-box--expanded"}${
        isElectron && isMac ? " menu-box--mac" : ""
      }`}
    >
      <div className="logo" onClick={handleLogoClick}></div>
      <DeviceStatus
        useSdkApi={props?.useSdkApi}
        mouseDeviceInfo={props?.mouseDeviceInfo}
        onDeviceListClick={handleLogoClick}
        sidebarCollapsed={menuFold}
      />
      <div className="menu-main" ref={menuMainRef}>
        {menuData?.map((item: MenuItem, key: number) => {
          const visibleChildren =
            item?.children?.filter((child) =>
              shouldShowMenuItem(child.path, menuCtx)
            ) ?? [];
          if (visibleChildren.length === 0) return null;
          const hideGroupTitle =
            menuCtx.otherMenuOnly && menuCtx.solutionRovider === "xingshan";
          return (
            <div
              className={`menu-list ${item.line && !hideGroupTitle ? "line" : ""}`}
              key={key}
            >
              {item.title && !hideGroupTitle ? (
                <div
                  className={`list-title ${item.title === "common.advancedConfig" ? "list-title--advanced" : ""}`}
                >
                  {getListTitle(item)}
                </div>
              ) : null}
              {item?.children ? (
                <div className="menu-child">
                  {visibleChildren.map((child: MenuItem, childKey: number) => {
                    const isChildActive = checkKey === child.key;
                    const isModalActive = child.modal && modalKey === child.key;
                    const itemDisabled = isMenuItemInteractionDisabled(child);
                    const isScreenWirelessBlocked =
                      child.path === "screen" &&
                      menuCtx.tftScreenWirelessBlocked;
                    const className =
                      `child-list child-item ${isChildActive ? "active" : ""} ` +
                      `${isModalActive ? "modal-active" : ""} ` +
                      `${itemDisabled ? "disabled" : ""}`;
                    const childTitleNode = (
                      <div
                        className={`child-title ${
                          isChildActive && !itemDisabled
                            ? "shared-active-glow"
                            : ""
                        }`}
                        onClick={() => menuCheck(child)}
                        aria-disabled={itemDisabled || undefined}
                      >
                        <span className="icon">
                          <Iconfont icon={child.icon || ""} />
                        </span>
                        <span className="text" style={{}}>
                          {getTitle(child.title)}
                        </span>
                        <span className="arrow">
                          <Iconfont icon="chevronright" />
                        </span>
                      </div>
                    );
                    return (
                      <div className={className} key={childKey}>
                        <div
                          className="menu-item-tooltip-host"
                          onMouseEnter={(e) => {
                            if (!menuFold || child.path === "boardConfig") {
                              return;
                            }
                            if (itemDisabled && !isScreenWirelessBlocked) {
                              return;
                            }
                            const r = (
                              e.currentTarget as HTMLDivElement
                            ).getBoundingClientRect();
                            setSidebarTooltip({
                              text: isScreenWirelessBlocked
                                ? screenWiredOnlyTip
                                : getTitle(child.title),
                              left: r.right,
                              top: r.top + r.height / 2,
                              wrapTwoLines: isScreenWirelessBlocked,
                            });
                          }}
                          onMouseLeave={() => setSidebarTooltip(null)}
                        >
                          {isScreenWirelessBlocked && !menuFold ? (
                            <Tooltip
                              text={screenWiredOnlyTip}
                              placement="right"
                              maxWidth={280}
                              usePortal
                              zIndex={200020}
                              className="menu-screen-wired-tip"
                              overlayClassName="menu-screen-wired-tip-portal"
                            >
                              {childTitleNode}
                            </Tooltip>
                          ) : (
                            childTitleNode
                          )}
                        </div>
                        {child?.modal ? (
                          <ChildModal
                            useSdkApi={props?.useSdkApi}
                            data={child}
                            mouseHIDHandle={props?.mouseHIDHandle}
                            mouseDeviceInfo={props?.mouseDeviceInfo}
                            open={isModalActive}
                            disabled={itemDisabled}
                            sidebarCollapsed={menuFold}
                          />
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
          );
        })}
        {/* <div className="menu-list">
          <div className="menu-child">
            <div className="child-list">
              <div className="child-title">
                <span className="icon"></span>
                <span className="text">快速配置</span>
                <span className="arrow"></span>
              </div>
              <div className="child-modal"></div>
            </div>
          </div>
        </div>
        <div className="menu-list line">
          <div className="menu-child">
            <div className="child-list">
              <div className="child-title">
                <span className="icon"></span>
                <span className="text">板载配置</span>
                <span className="arrow"></span>
              </div>
              <div className="child-modal"></div>
            </div>
          </div>
        </div>
        <div className="menu-list">
          <div className="list-title">高级配置</div>
          <div className="menu-child">
            <div className="child-list">
              <div className="child-title">
                <span className="icon"></span>
                <span className="text">灯光设置</span>
                <span className="arrow"></span>
              </div>
            </div>
            <div className="child-list">
              <div className="child-title">
                <span className="icon"></span>
                <span className="text">触发设置</span>
                <span className="arrow"></span>
              </div>
            </div>
            <div className="child-list">
              <div className="child-title">
                <span className="icon"></span>
                <span className="text">改键设置</span>
                <span className="arrow"></span>
              </div>
            </div>
            <div className="child-list">
              <div className="child-title">
                <span className="icon"></span>
                <span className="text">高级按键</span>
                <span className="arrow"></span>
              </div>
            </div>
          </div>
        </div> */}
      </div>
      <div
        className={`menu-footer ${menuFold ? "menu-footer--stacked" : "menu-footer--row"}`}
      >
        <div className="menu-footer-actions">
          {showBetaFeedback ? (
            <div
              className={`feedback menu-footer-feedback ${menuFold ? "menu-item-tooltip-host" : ""}`}
              onClick={handleBetaFeedbackClick}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleBetaFeedbackClick();
                }
              }}
              onMouseEnter={(e) => {
                if (!menuFold) return;
                const r = (
                  e.currentTarget as HTMLDivElement
                ).getBoundingClientRect();
                setSidebarTooltip({
                  text: t("system.help.betaFeedback"),
                  left: r.right,
                  top: r.top + r.height / 2,
                });
              }}
              onMouseLeave={() => setSidebarTooltip(null)}
            >
              <div className="icon-text">
                <span className="icon">
                  <img src={feedbackImg} alt="" />
                </span>
                <span className="text">{t("system.help.betaFeedback")}</span>
              </div>
            </div>
          ) : (
            <div
              className={`feedback menu-footer-feedback ${menuFold ? "menu-item-tooltip-host" : ""}`}
              onClick={() => handleFeedbackClick()}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleFeedbackClick();
                }
              }}
              onMouseEnter={(e) => {
                if (!menuFold) return;
                const r = (
                  e.currentTarget as HTMLDivElement
                ).getBoundingClientRect();
                setSidebarTooltip({
                  text: t("system.help.faq"),
                  left: r.right,
                  top: r.top + r.height / 2,
                });
              }}
              onMouseLeave={() => setSidebarTooltip(null)}
            >
              <div className="icon-text">
                <span className="icon">
                  <img src={feedbackImg} alt="" />
                </span>
                <span className="text">{t("system.help.faq")}</span>
              </div>
            </div>
          )}
        </div>
        <button
          type="button"
          className="menu-footer-toggle"
          aria-label={
            menuFold ? t("common.sidebarExpand") : t("common.sidebarCollapse")
          }
          title={
            menuFold ? t("common.sidebarExpand") : t("common.sidebarCollapse")
          }
          onClick={handleSidebarToggleClick}
        >
          <Iconfont icon={menuFold ? "open" : "fold"} />
        </button>
      </div>
      {menuFold &&
        sidebarTooltip &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className={`menu-sidebar-flyout menu-sidebar-flyout--portal${
              sidebarTooltip.wrapTwoLines
                ? " menu-sidebar-flyout--portal--wrap-2"
                : ""
            }`}
            style={{
              position: "fixed",
              left: sidebarTooltip.left,
              top: sidebarTooltip.top,
              transform: "translateY(-50%)",
            }}
            role="tooltip"
          >
            {sidebarTooltip.text}
          </div>,
          document.body
        )}
    </div>
  );
});

Menu.displayName = "Menu";
